import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';
import { AuthContext } from 'authentication/AuthContext';
import usePersistentFilters from 'hooks/usePersistentFilters';
import { useGetCustomerAuthorizationFilterValuesQuery } from './customerAuthGridEndpoints';
import { DEFAULT_STATUS } from './customerAuthFilterConstants';

const INITIAL_FILTER_STATE = {
  affiliateValue: null,
  authTypes: [],
  controlTypes: [],
  locations: [],
  products: [],
  status: DEFAULT_STATUS.value,
};

const toValueList = list => (list || []).map(item => item.value).sort();

const useCustomerAuthFilters = () => {
  const { affiliates = [] } = useContext(AuthContext);

  const affiliateOptions = useMemo(
    () => affiliates.filter(affiliate => affiliate?.supplier),
    [affiliates]
  );

  const sortedAffiliateOptions = useMemo(
    () =>
      [...affiliateOptions].sort((a, b) =>
        (a.customerIdName ?? '').localeCompare(b.customerIdName ?? '', undefined, {
          numeric: true,
        })
      ),
    [affiliateOptions]
  );

  const affiliateDisplayOptions = useMemo(
    () =>
      sortedAffiliateOptions.map(a => ({
        ...a,
        label: a.customerNameLong,
        value: `${a.customerIdName}-${a.dataCompanyId}`,
      })),
    [sortedAffiliateOptions]
  );

  const showAffiliateFilter = affiliateDisplayOptions.length > 1;

  const [persistedFilters, setPersistedFilters] = usePersistentFilters(
    'customerAuthorizationsFilters',
    INITIAL_FILTER_STATE
  );

  const resolveAffiliate = useCallback(
    value => {
      const match = affiliateDisplayOptions.find(a => a.value === value);
      return match || affiliateDisplayOptions[0] || null;
    },
    [affiliateDisplayOptions]
  );

  const selectedAffiliate = useMemo(
    () => resolveAffiliate(persistedFilters.affiliateValue),
    [resolveAffiliate, persistedFilters.affiliateValue]
  );

  const [draftFilters, setDraftFilters] = useState({
    authTypes: persistedFilters.authTypes || [],
    controlTypes: persistedFilters.controlTypes || [],
    locations: persistedFilters.locations || [],
    products: persistedFilters.products || [],
    search: '',
    status: persistedFilters.status || DEFAULT_STATUS.value,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    affiliate: selectedAffiliate,
    authTypes: persistedFilters.authTypes || [],
    controlTypes: persistedFilters.controlTypes || [],
    locations: persistedFilters.locations || [],
    products: persistedFilters.products || [],
    search: '',
    status: persistedFilters.status || DEFAULT_STATUS.value,
  });

  useEffect(() => {
    setAppliedFilters(prev => ({ ...prev, affiliate: selectedAffiliate }));
  }, [selectedAffiliate]);

  const filterValuesQueryParams = useMemo(() => {
    if (!selectedAffiliate?.customerIdName || !selectedAffiliate?.dataCompanyId) return null;
    return {
      supplier: {
        companyCustomerIdName: selectedAffiliate.customerIdName,
        dataCompanyId: selectedAffiliate.dataCompanyId,
      },
    };
  }, [selectedAffiliate]);

  const { data: filterValues = {}, isFetching: filterValuesLoading } =
    useGetCustomerAuthorizationFilterValuesQuery(
      { params: filterValuesQueryParams },
      { skip: !filterValuesQueryParams }
    );

  const locationOptions = useMemo(
    () =>
      (filterValues.terminals || []).map(({ terminalIdName, terminalNameLong }) => ({
        label: terminalNameLong,
        value: terminalIdName,
      })),
    [filterValues.terminals]
  );
  const locationsLoading = filterValuesLoading;

  const productOptions = useMemo(
    () =>
      (filterValues.products || []).map(({ productIdName, productDisplayName }) => ({
        label: productDisplayName,
        value: productIdName,
      })),
    [filterValues.products]
  );
  const productsLoading = filterValuesLoading;

  const hasChanges = useMemo(
    () =>
      !R.equals(toValueList(draftFilters.locations), toValueList(appliedFilters.locations)) ||
      !R.equals(toValueList(draftFilters.products), toValueList(appliedFilters.products)) ||
      !R.equals(toValueList(draftFilters.controlTypes), toValueList(appliedFilters.controlTypes)) ||
      !R.equals(toValueList(draftFilters.authTypes), toValueList(appliedFilters.authTypes)) ||
      draftFilters.status !== appliedFilters.status,
    [draftFilters, appliedFilters]
  );

  const isOnDefaultState =
    !draftFilters.locations.length &&
    !draftFilters.products.length &&
    !draftFilters.controlTypes.length &&
    !draftFilters.authTypes.length &&
    draftFilters.status === DEFAULT_STATUS.value &&
    !draftFilters.search;

  const setAffiliateFilter = useCallback(
    value => {
      const resetState = {
        authTypes: [],
        controlTypes: [],
        locations: [],
        products: [],
        search: '',
        status: DEFAULT_STATUS.value,
      };
      setDraftFilters(resetState);
      setAppliedFilters({ affiliate: value, ...resetState });
      setPersistedFilters({
        affiliateValue: value?.value ?? null,
        authTypes: [],
        controlTypes: [],
        locations: [],
        products: [],
        status: DEFAULT_STATUS.value,
      });
    },
    [setPersistedFilters]
  );

  const setLocationFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, locations: value || [] })),
    []
  );

  const setProductFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, products: value || [] })),
    []
  );

  const setControlTypeFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, controlTypes: value || [] })),
    []
  );

  const setAuthTypeFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, authTypes: value || [] })),
    []
  );

  const setStatusFilter = useCallback(
    value => setDraftFilters(prev => ({ ...prev, status: value || DEFAULT_STATUS.value })),
    []
  );

  const setSearch = useCallback(value => setDraftFilters(prev => ({ ...prev, search: value })), []);

  const handleApply = useCallback(() => {
    setAppliedFilters(prev => ({
      ...prev,
      authTypes: draftFilters.authTypes,
      controlTypes: draftFilters.controlTypes,
      locations: draftFilters.locations,
      products: draftFilters.products,
      search: draftFilters.search,
      status: draftFilters.status,
    }));
    setPersistedFilters({
      affiliateValue: selectedAffiliate?.value ?? null,
      authTypes: draftFilters.authTypes,
      controlTypes: draftFilters.controlTypes,
      locations: draftFilters.locations,
      products: draftFilters.products,
      status: draftFilters.status,
    });
  }, [draftFilters, selectedAffiliate, setPersistedFilters]);

  const handleReset = useCallback(() => {
    const resetState = {
      authTypes: [],
      controlTypes: [],
      locations: [],
      products: [],
      search: '',
      status: DEFAULT_STATUS.value,
    };
    setDraftFilters(resetState);
    setAppliedFilters(prev => ({ ...prev, ...resetState }));
    setPersistedFilters({
      affiliateValue: selectedAffiliate?.value ?? null,
      authTypes: [],
      controlTypes: [],
      locations: [],
      products: [],
      status: DEFAULT_STATUS.value,
    });
  }, [selectedAffiliate, setPersistedFilters]);

  return {
    affiliateDisplayOptions,
    appliedFilters,
    draftFilters,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
    locationOptions,
    locationsLoading,
    productOptions,
    productsLoading,
    selectedAffiliate,
    setAffiliateFilter,
    setAuthTypeFilter,
    setControlTypeFilter,
    setLocationFilter,
    setProductFilter,
    setSearch,
    setStatusFilter,
    showAffiliateFilter,
  };
};

export default useCustomerAuthFilters;
